<script setup lang="ts">
/**
 * App header — faithful port of components.jsx::AppHeader.
 * Same class names (.app-header, __brand, __divider, __product, __build,
 * __nav, __tools, __icon-btn, __status, __user, __avatar) from app.css.
 * Mock Kate Schultz replaced with live auth.email; ⌘K wired through commandPalette.
 */
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import Icon from '@/components/shared/Icon.vue';
import { commandPalette } from '@/composables/useCommandPalette';
import { toast } from '@/composables/useToast';
import { useAuthStore } from '@/stores/auth';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { useHelpStore } from '@/stores/help';
import { useUiStore } from '@/stores/ui';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const featureFlags = useFeatureFlagsStore();
const help = useHelpStore();
const ui = useUiStore();

// Bundle's bake-time SHA — injected by Dockerfile ARG RAILWAY_GIT_COMMIT_SHA
// → VITE_BUILD_SHA in the frontend-build stage. 'dev' for local builds without
// Railway env. Stays constant for the life of this bundle in the browser.
const bundleSha = ((import.meta as unknown as { env: { VITE_BUILD_SHA?: string } }).env.VITE_BUILD_SHA || 'dev');
const bundleShort = bundleSha === 'dev' ? 'dev' : bundleSha.slice(0, 7);

// Api's runtime SHA — fetched on mount from /v1/version (the unauthenticated
// build-identity endpoint added at the same time as this chip). When the api
// commit differs from the bundle, the user is looking at a cached frontend
// older than the live deploy → show an amber refresh prompt.
const apiSha = ref<string>('');
const apiShaShort = computed(() => apiSha.value.slice(0, 7));
const versionMismatch = computed(() =>
  apiSha.value !== '' && bundleSha !== 'dev' && apiShaShort.value !== bundleShort,
);

onMounted(async () => {
  try {
    // Plain fetch — /v1/version is unauthenticated, no need for the http() wrapper.
    const r = await fetch('/v1/version');
    if (!r.ok) return;
    // The API returns the standard envelope `{success, data, error, meta}`
    // (see app/api/wrappers.py). Unwrap before reading flags. The pre-2026-06-06
    // code read `data?.commit` / `data?.split_merge_enabled` against the
    // ENVELOPE, which always resolved to undefined → flags never lit up in
    // the browser even when the backend had them on. Bug fix: read from
    // envelope.data.
    const env = await r.json().catch(() => null) as
      | { data?: { commit?: string; help_ask_ai_enabled?: boolean; split_merge_enabled?: boolean; bulk_reassign_enabled?: boolean; segment_reorder_enabled?: boolean } }
      | null;
    const data = env?.data;
    apiSha.value = data?.commit || '';
    // Phase 2 — Help Center Ask AI tab visibility (backend SSOT).
    if (typeof data?.help_ask_ai_enabled === 'boolean') {
      help.setAskEnabled(data.help_ask_ai_enabled);
    }
    // Phase 3.5 + 4 — split/merge UI gating (backend SSOT).
    featureFlags.setSplitMergeEnabled(data?.split_merge_enabled ?? false);
    // Bulk reassign UI gating (backend SSOT).
    featureFlags.setBulkReassignEnabled(data?.bulk_reassign_enabled ?? false);
    // Segment drag-drop reorder UI gating (backend SSOT).
    featureFlags.setSegmentReorderEnabled(data?.segment_reorder_enabled ?? false);
  } catch {
    /* silent — version chip is non-essential */
  }
});

async function copyBuildInfo(): Promise<void> {
  const info = `bundle=${bundleShort} api=${apiShaShort.value || 'unknown'}`;
  try {
    await navigator.clipboard.writeText(info);
    toast.push(`Copied: ${info}`, { tone: 'info' });
  } catch {
    toast.push(info, { tone: 'info' });
  }
}

async function reloadForLatest(): Promise<void> {
  // Force-bust the cache so the new index.html + bundle SHA come through.
  window.location.reload();
}

function isActive(prefixes: string[]): boolean {
  return prefixes.some(p => route.path.startsWith(p));
}

const userName = computed(() => {
  if (!auth.email) return 'Guest';
  // Convert kate.schultz@vin.com → Kate Schultz
  const handle = auth.email.split('@')[0] ?? '';
  return handle.split(/[._-]/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
});
const userInitials = computed(() => {
  if (!auth.email) return '?';
  const handle = auth.email.split('@')[0] ?? '';
  const parts = handle.split(/[._-]/);
  if (parts.length >= 2) return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  return handle.slice(0, 2).toUpperCase();
});

function onSearch(): void { commandPalette.open(); }
function fontDelta(delta: number): void {
  // Drive the persisted transcript font scale (consumed by .segment__text via
  // --transcript-font-scale). The previous version set documentElement
  // font-size, which the transcript's fixed-px text ignored — so A+/A- looked
  // like a no-op. Step by 0.1; the store clamps to a readable range.
  ui.setFontScale((ui.fontScale ?? 1) + (delta > 0 ? 0.1 : -0.1));
  toast.push(`Transcript font ${delta > 0 ? 'larger' : 'smaller'}`, { tone: 'info' });
}
function onLogout(): void {
  auth.logout();
  toast.push('Signed out', { tone: 'success' });
  router.push('/login');
}
</script>

<template>
  <header class="app-header" data-screen-label="App Header">
    <RouterLink to="/sessions" class="app-header__brand" aria-label="roundstranscripttool.vin home">
      <img src="/assets/VIN-light.svg" alt="VIN" />
      <span class="app-header__divider" />
      <span class="app-header__product">roundstranscripttool<strong>.vin</strong></span>
    </RouterLink>
    <span
      class="app-header__build"
      :title="`Bundle: ${bundleSha}\nApi: ${apiSha || 'unknown'}\nClick to copy`"
      :style="{ cursor: 'pointer', fontFamily: 'var(--font-mono)' }"
      @click="copyBuildInfo"
    >{{ bundleShort }}</span>
    <button
      v-if="versionMismatch"
      class="app-header__build"
      :style="{
        background: 'rgba(217, 119, 6, 0.18)',
        color: 'var(--color-amber)',
        border: '1px solid rgba(217, 119, 6, 0.55)',
        borderRadius: '999px',
        padding: '2px 10px',
        marginLeft: '6px',
        cursor: 'pointer',
        fontFamily: 'var(--font-mono)',
        fontSize: '11px',
        fontWeight: 700,
      }"
      :title="`API is on ${apiSha} but your bundle is ${bundleSha}. Click to hard-reload.`"
      @click="reloadForLatest"
    >⟳ reload for {{ apiShaShort }}</button>

    <nav class="app-header__nav" aria-label="Primary">
      <RouterLink to="/dashboard"     :class="{ 'is-active': isActive(['/dashboard']) }">Dashboard</RouterLink>
      <RouterLink to="/sessions"      :class="{ 'is-active': isActive(['/sessions', '/s/', '/v/', '/e/', '/p/']) }">Sessions</RouterLink>
      <RouterLink to="/upload"        :class="{ 'is-active': isActive(['/upload']) }">Upload</RouterLink>
      <RouterLink to="/improvements"  :class="{ 'is-active': isActive(['/improvements']) }">Improvements</RouterLink>
      <RouterLink to="/settings"      :class="{ 'is-active': isActive(['/settings', '/audit', '/gcs']) }">Settings</RouterLink>
    </nav>

    <div class="app-header__tools" aria-label="Quick tools">
      <button class="app-header__icon-btn" title="Search routes (⌘K)" aria-label="Search" data-test-id="topbar-search" @click="onSearch">
        <Icon name="search" :size="14" />
      </button>
      <span class="app-header__divider" />
      <button class="app-header__icon-btn app-header__icon-btn--mono" title="Decrease font size" aria-label="Decrease font size" data-test-id="topbar-font-decrease" @click="fontDelta(-1)">A−</button>
      <button class="app-header__icon-btn app-header__icon-btn--mono" title="Increase font size" aria-label="Increase font size" data-test-id="topbar-font-increase" @click="fontDelta(1)">A+</button>
      <span class="app-header__divider" />
      <button class="app-header__icon-btn app-header__icon-btn--mono" title="Open Help" aria-label="Open help center" data-test-id="topbar-help" @click="help.toggle">?</button>
    </div>

    <div class="app-header__user" :title="auth.email ? `Logged in as ${userName}` : 'Not signed in'">
      <span class="app-header__avatar">{{ userInitials }}</span>
      <span style="margin-right: 4px;">{{ userName }}</span>
      <button
        v-if="auth.isAuthenticated"
        class="app-header__icon-btn app-header__icon-btn--mono"
        title="Logout"
        data-test-id="topbar-logout"
        style="margin-left: 8px;"
        @click="onLogout"
      >Logout</button>
    </div>
  </header>
</template>
